/**
 * Per-view registry context + ref-callback hooks + the highlight-manager hook.
 * The registry is created once per reader view; word/sentence/doc-pack render
 * leaves register their DOM element through a stable ref callback (React 19
 * ref-cleanup), and the manager (a rAF-scheduled `createHighlightController`)
 * toggles `data-active` on exactly the active elements — no per-word React
 * state, no document-wide `querySelectorAll`.
 */

import {
  createContext,
  use,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
} from 'react';
import { useAudioPoolPlayerMethods } from '@listening/frontend/audio-chunk-player/pool-player-context';
import { createRafScheduler } from '@listening/frontend/audio-chunk-player/use-playback-derived';
import {
  useAudioChunkSelector,
  useCurrentAudioChunkQuery,
} from '@/lib/audio-chunk-player';
import { createHighlightController } from './word-highlight-manager';
import {
  ReaderActionAnchorContext,
  registerReaderActionText,
  type ReaderActionText,
} from '../reader-action-anchor';
import type {
  LegacySentenceKey,
  WordElementRegistry,
} from './word-element-registry';

export const HighlightRegistryContext =
  createContext<WordElementRegistry<HTMLElement> | null>(null);

/** `text` keyed by its fields: views rebuild the object on every zoom step, and
 * a new identity would re-register every word on the page. */
function useStableReaderActionText(text: ReaderActionText | undefined) {
  const word = text?.word;
  const passage = text?.passage;
  const wordStart = text?.wordStart;
  return useMemo(
    () =>
      word === undefined || passage === undefined || wordStart === undefined
        ? undefined
        : { word, passage, wordStart },
    [word, passage, wordStart],
  );
}

/** Stable ref callback registering a legacy word (+ its sentence key, so the
 * manager can resolve the follow anchor). */
export function useLegacyWordRegistryRef(
  chunkId: string,
  wordIdxInChunk: number,
  sentence: LegacySentenceKey,
  text?: ReaderActionText,
) {
  const registry = use(HighlightRegistryContext);
  const stableText = useStableReaderActionText(text);
  const { chapterId, paragraphIdx, sentenceIdx } = sentence;
  return useCallback(
    (el: HTMLElement | null) => {
      if (!el || !registry) return;
      return registerReaderActionText(
        el,
        stableText,
        registry.registerLegacyWord({ chunkId, wordIdxInChunk }, el, {
          chapterId,
          paragraphIdx,
          sentenceIdx,
        }),
      );
    },
    [
      registry,
      chunkId,
      wordIdxInChunk,
      chapterId,
      paragraphIdx,
      sentenceIdx,
      stableText,
    ],
  );
}

export function useLegacySentenceRegistryRef(sentence: LegacySentenceKey) {
  const registry = use(HighlightRegistryContext);
  const { chapterId, paragraphIdx, sentenceIdx } = sentence;
  return useCallback(
    (el: HTMLElement | null) => {
      if (!el || !registry) return;
      return registry.registerLegacySentence(
        { chapterId, paragraphIdx, sentenceIdx },
        el,
      );
    },
    [registry, chapterId, paragraphIdx, sentenceIdx],
  );
}

export function useDocPackWordRegistryRef(
  page: number,
  charStart: number,
  charEnd: number,
  text?: ReaderActionText,
) {
  const registry = use(HighlightRegistryContext);
  const stableText = useStableReaderActionText(text);
  return useCallback(
    (el: HTMLElement | null) => {
      if (!el || !registry) return;
      return registerReaderActionText(
        el,
        stableText,
        registry.registerDocPackWord(page, [charStart, charEnd], el),
      );
    },
    [registry, page, charStart, charEnd, stableText],
  );
}

/** Drives DOM-based highlighting for a view. Reads chunk/ttsId reactively (they
 * change only at chunk boundaries) and the playback position through the
 * methods-only pool context (no per-tick re-render of the mounting view). */
export function useWordHighlightManager({
  registry,
  onAnchorElementChange,
  enabled,
}: {
  registry: WordElementRegistry<HTMLElement>;
  onAnchorElementChange: (el: HTMLElement | null) => void;
  enabled: boolean;
}) {
  const ttsChunk = useCurrentAudioChunkQuery().data;
  const ttsId = useAudioChunkSelector((s) => s.ttsId);
  const getPositionMs = useAudioPoolPlayerMethods().getPositionMs;
  const actionAnchor = use(ReaderActionAnchorContext);

  const getChunk = useEffectEvent(() => ttsChunk);
  const getTtsId = useEffectEvent(() => ttsId);
  const readPositionMs = useEffectEvent(() => getPositionMs());
  const anchorElementChanged = useEffectEvent((el: HTMLElement | null) =>
    onAnchorElementChange(el),
  );
  const activeWordElementChanged = useEffectEvent(
    (el: HTMLElement | null, previous: HTMLElement | null) => {
      if (previous) actionAnchor?.clearAnchor(previous);
      if (el) actionAnchor?.setAnchor(el);
    },
  );

  useEffect(() => {
    if (!enabled) return;
    let previousActiveWord: HTMLElement | null = null;
    const controller = createHighlightController<HTMLElement>({
      registry,
      getChunk,
      getTtsId,
      getPositionMs: readPositionMs,
      setActive: (el, active) => {
        if (active) el.dataset.active = 'true';
        else delete el.dataset.active;
      },
      onAnchorElementChange: anchorElementChanged,
      onActiveWordElementChange: (el) => {
        activeWordElementChanged(el, previousActiveWord);
        previousActiveWord = el;
      },
      scheduler: createRafScheduler(),
    });
    controller.start();
    return () => controller.stop();
  }, [registry, enabled]);
}
